import {
  Skeleton,
  CalendarIcon,
  Button,
  EditIcon,
  Text,
  DrawerStepper,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import {
  formatLongDate,
  onErrorMessage,
  onSuccessMessage,
  isSaleOrderEditable,
} from '@/helpers'
import { CardInfo } from '@/components/card-info'

import styles from './card-order-delivery-date.module.css'

import type { CardOrderDeliveyDateProps } from './card-order-delivery-date.type'
import { OrderShipmentDeliveyAtForm } from '@/components/forms'
import { useEffect, useState } from 'react'
import { LogisticShipmentProps, SaleOrderProps } from '@/types'
import { QUERIES_ADMIN, useOrderCenterSaleOrderUpdate } from '@/services'
import { useQueryClient } from '@tanstack/react-query'

const CardOrderDeliveyDateDefault = ({
  shipments,
  saleOrderStatus,
  saleOrderUuid,
}: CardOrderDeliveyDateProps) => {
  const { t } = useTranslation('componentCardsOrderCenterShow')

  const [isOpen, setIsOpen] = useState(false)

  const [values, setValues] = useState<Partial<SaleOrderProps>>({
    shipments: shipments?.map((shipment) => ({
      uuid: shipment.uuid,
      deliveredAt: shipment.deliveredAt,
    })) as LogisticShipmentProps[],
  })

  const queryClient = useQueryClient()

  const { mutateAsync } = useOrderCenterSaleOrderUpdate(saleOrderUuid || '')

  const isEditable = isSaleOrderEditable(saleOrderStatus)

  const handleClose = () => {
    setIsOpen(false)
  }

  const onSubmit = async () => {
    const response = await mutateAsync(values)

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrderUuid],
      })
      onSuccessMessage(
        t('orderStatusesSuccessMessageTitle'),
        t('orderStatusesSuccessMessageDescription'),
      )

      setIsOpen(false)
    } else {
      onErrorMessage(
        t('orderStatusesErrorMessageTitle'),
        t('orderStatusesErrorMessageDescription'),
      )
    }
  }

  useEffect(() => {
    if (!isOpen) return setValues({})

    setValues({
      shipments: shipments?.map((shipment) => ({
        uuid: shipment.uuid,
        deliveredAt: shipment.deliveredAt,
      })) as LogisticShipmentProps[],
    })
  }, [isOpen])

  return (
    <div>
      <CardInfo
        icon={<CalendarIcon />}
        title={t('orderDeliveryDateTitle')}
        className={styles.core}
        actions={
          isEditable && (
            <Button
              variant="ghost"
              className={styles.edit}
              size="small"
              onClick={() => setIsOpen(true)}
            >
              {t('editLabel')}
              <EditIcon />
            </Button>
          )
        }
      >
        {shipments?.map(
          (shipment) =>
            shipment?.deliveredAt && (
              <div key={shipment?.uuid} className={styles['date-info']}>
                <Text variant="text-base">
                  {formatLongDate(shipment.deliveredAt)}
                </Text>
              </div>
            ),
        )}

        {shipments?.length === 1 && !shipments?.[0]?.deliveredAt && (
          <Text variant="text-base">{t('notInformedLabel')}</Text>
        )}
      </CardInfo>
      <DrawerStepper
        isOpen={isOpen}
        onHandleOpen={handleClose}
        initialStep="root"
        title={t('orderDeliveryDateDrawerTitle')}
        translations={{
          optional: '',
        }}
        steps={{
          root: {
            backNavigate: 'root',
            isOptional: false,
            component: (
              <OrderShipmentDeliveyAtForm
                onBack={handleClose}
                values={values}
                onChangeValues={(value) => setValues(value)}
                onHandleSubmit={onSubmit}
              />
            ),
          },
        }}
      />
    </div>
  )
}

const CardOrderDeliveyDateLoading = () => {
  const { t } = useTranslation('componentCardsOrderCenterShow')
  return (
    <CardInfo
      icon={<CalendarIcon />}
      title={t('orderDeliveryDateTitle')}
      className={styles.core}
    >
      <Skeleton width="60%" height="1rem" />
    </CardInfo>
  )
}

export const CardOrderDeliveyDate = (props: CardOrderDeliveyDateProps) => {
  if (props.isLoading) {
    return <CardOrderDeliveyDateLoading />
  }

  return <CardOrderDeliveyDateDefault {...props} />
}
